import React, { useContext } from 'react';

import {
  IconButton,
  UploadFile,
  View,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Stack,
  Const,
  CloseIcon,
} from '@gogame/components';
import { FormContext, FormField } from '@gogame/form';
import { useProcessAsync } from '@gogame/hooks-ts';
import { useCurrencyFormat, AmountFormatter } from '@gogame/intl-ts';

import { CREDIT_MEMO } from 'scenes/FinancialResults/components/FinancialResultForm/InvoicesFieldArray/constants';
import {
  INVOICE_CURRENCY_CODE,
  INVOICE_AMOUNT,
  INVOICE_TYPE,
  BUSINESS_PARTNER,
  AFFILIATES,
} from 'scenes/FinancialResults/components/FinancialResultForm/constants';
import {
  loadCurrencies,
  loadInvoiceTypes,
} from 'scenes/FinancialResults/sources/fields';

const CURRENCY_DEPENDENCIES = [BUSINESS_PARTNER, AFFILIATES];

const Invoice = ({
  id,
  name,
  amountBase,
  currencyCodeBase,
  currencyCode,
  type,
  actualSpend,
  error,
  onRemove,
  onReplace,
}) => {
  const [loading, processAsync] = useProcessAsync();
  const currencyFormat = useCurrencyFormat({ currency: currencyCodeBase });
  const { values } = useContext(FormContext);
  const handleRemove = async () => {
    await processAsync(onRemove());
  };

  const handleReplace = async (e, filesArray) => {
    await processAsync(onReplace(e, filesArray));
  };

  return (
    <View fetching={loading}>
      <Card elevation={6}>
        <CardHeader
          title={
            <Typography
              noWrap={true}
              tooltipProps={{
                wrapper: true,
                wrapperComponent: 'div',
                wrapperZeroMinWidth: true,
              }}
            >
              {name}
            </Typography>
          }
          disableTypography={true}
          action={
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <UploadFile
                tooltip={'.pdf, .png, .jpg, .jpeg, .doc, .docx, .xls, .xlsx'}
                label={'Replace'}
                accept={
                  '.pdf, image/png, image/jpg, image/jpeg, .doc, .docx, .xls, .xlsx'
                }
                onUpload={handleReplace}
                variant={'outlined'}
              />
              <IconButton
                icon={<CloseIcon />}
                tooltip={'Remove'}
                onClick={handleRemove}
              />
            </Stack>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <FormField.Select
                label={'Type'}
                dense={true}
                variant={'outlined'}
                name={`${id}[${INVOICE_TYPE}]`}
                onDataLoad={({ data, value, form, name }) => {
                  if (data && !value) {
                    form.setFieldValue(name, data[0].id, false);
                  }
                }}
                placeholder={'Select type'}
                source={loadInvoiceTypes}
                lazy={!name}
                padding={0}
              />
            </Grid>
            <Grid item xs={2}>
              <FormField.Select
                label={'Invoice currency'}
                variant={'outlined'}
                dense={true}
                name={`${id}[${INVOICE_CURRENCY_CODE}]`}
                placeholder={'Select currency'}
                source={loadCurrencies}
                depends={CURRENCY_DEPENDENCIES}
                active={!!values[BUSINESS_PARTNER]}
                padding={0}
              />
            </Grid>
            <Grid item xs={2}>
              <FormField.TextField
                type={'currency'}
                variant={'outlined'}
                currency={currencyCode}
                helper={
                  amountBase
                    ? `(${currencyFormat(amountBase)})`
                    : type === CREDIT_MEMO
                      ? 'Should be < 0'
                      : 'Should be > 1'
                }
                label={'Invoice amount'}
                dense={true}
                name={`${id}[${INVOICE_AMOUNT}]`}
                placeholder={'0'}
                debounceTimeout={800}
                padding={0}
                required={true}
              />
            </Grid>
            <Grid item xs={2}>
              <Const label={'GG spend'} data-cy-role={'gg-spend'}>
                {actualSpend?.amount > 0 ? (
                  <AmountFormatter {...actualSpend} />
                ) : (
                  '—'
                )}
              </Const>
            </Grid>

            {error ? (
              <Grid item xs={12}>
                <Typography color={'error'}>{error}</Typography>
              </Grid>
            ) : null}
          </Grid>
        </CardContent>
      </Card>
    </View>
  );
};

export default Invoice;
